import React from 'react'
import { useLocation } from 'react-router'
import { CVRCompanyDetails, CompanyFinancials } from '../../types'
import { Badge, CountryFlagIcon } from '../ui'
import {
  companyCountryFlagCode,
  companyStatusBadgeTone,
  formatDate,
  formatStatusLabel,
} from '../../utils/format'
import { dataCvrCompanyUrl } from '../../utils/cvrRegisterLinks'
import ProfileActionLinks from './ProfileActionLinks'
import CompanyKeyFiguresLine from './financial/CompanyKeyFiguresLine'

/**
 * Top-of-page identifier strip: kicker, mono CVR, copy/share actions,
 * company `<h1>`, and status / dates. Copy confirmations use the global
 * toast provider (`showToast`). Pass `omitTitle` only if the parent
 * renders the company name elsewhere (unusual; company profile uses the
 * full bar on the company profile page).
 */
interface IdentityBarProps {
  company: CVRCompanyDetails
  /** When true, skips the primary company name `<h1>` (rendered elsewhere by the parent). */
  omitTitle?: boolean
  /** Optional control aligned with the company name (e.g. watchlist). */
  titleAction?: React.ReactNode
  financials?: CompanyFinancials | null
  financialsLoading?: boolean
}

const IdentityBar: React.FC<IdentityBarProps> = ({
  company,
  omitTitle = false,
  titleAction,
  financials = null,
  financialsLoading = false,
}) => {
  const location = useLocation()
  const statusLabel = formatStatusLabel(company.status)
  const statusTone = companyStatusBadgeTone(company.status)
  const startDate = formatDate(company.startdato)

  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${location.pathname}${location.search}`
      : `${location.pathname}${location.search}`

  const cvrStr = String(company.cvrNummer ?? '').trim()
  const datacvrHref = dataCvrCompanyUrl(company.cvrNummer)
  const flagCode = companyCountryFlagCode(company)

  return (
    <header className="pb-4">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm text-ink-500 mb-2">
        <span className="font-mono text-xs uppercase tracking-widest text-signal">
          Virksomhed
        </span>
        <span className="font-mono inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          <span>CVR {cvrStr || '—'}</span>
          <ProfileActionLinks
            copyValue={cvrStr}
            copyAriaLabel="Kopiér CVR-nummer"
            copyTitle="Kopiér CVR-nummer"
            copySuccessMessage="CVR-nummer kopieret."
            shareUrl={shareUrl}
            datacvrHref={datacvrHref}
            datacvrAriaLabel="Åbn virksomhed på DataCVR i nyt vindue"
          />
        </span>
      </div>

      {!omitTitle && (
        <div
          className="flex items-start justify-between gap-x-4 gap-y-2"
          data-testid="profile-title-row"
        >
          <h1 className="font-serif text-3xl sm:text-4xl text-ink leading-tight min-w-0 flex-1 inline-flex items-baseline gap-2 flex-wrap">
            {company.navn || <em className="text-ink-400">Uden navn</em>}
            {flagCode && (
              <CountryFlagIcon code={flagCode} className="text-2xl sm:text-3xl" />
            )}
          </h1>
          {titleAction}
        </div>
      )}

      <dl className={`flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm ${omitTitle ? 'mt-0' : 'mt-3'}`}>
        <div className="flex items-baseline gap-2">
          <dt className="text-ink-500">Status</dt>
          <dd>
            <Badge tone={statusTone}>{statusLabel}</Badge>
          </dd>
        </div>

        {startDate && (
          <div className="flex items-baseline gap-2">
            <dt className="text-ink-500">Stiftet</dt>
            <dd className="text-ink">{startDate}</dd>
          </div>
        )}

        {company.ansatte !== undefined && company.ansatte !== null && (
          <div className="flex items-baseline gap-2">
            <dt className="text-ink-500">Ansatte</dt>
            <dd className="text-ink tabular-nums">{company.ansatte}</dd>
          </div>
        )}

        {(financialsLoading || financials?.revenue || financials?.profit) && (
          <div className="flex items-baseline gap-2">
            <dt className="text-ink-500">Omsætning/resultat</dt>
            <dd className="text-ink tabular-nums" data-testid="company-financial-summary">
              {financialsLoading ? (
                <span className="text-ink-400">…</span>
              ) : financials ? (
                <CompanyKeyFiguresLine financials={financials} />
              ) : null}
            </dd>
          </div>
        )}
      </dl>
    </header>
  )
}

export default IdentityBar
